import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mock';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';

const Services = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Services Expertise Comptable Lyon | MMD CONSEIL - Fiscalité, Paie, Conseil"
        description="Services d'expertise comptable à Lyon : tenue comptabilité, déclarations fiscales, gestion paie, conseil stratégique PME. MMD CONSEIL vous accompagne dans votre développement."
        keywords="services expertise comptable Lyon, tenue comptabilité PME, déclarations fiscales Lyon, gestion paie entreprise, conseil fiscal Lyon, liasse fiscale, expert comptable services"
        url={getFullUrl("/services")}
      />
      
      {/* Hero Section */}
      <section className="hero-gradient py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white text-primary-blue px-4 py-2">
              Nos Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
              Un Accompagnement Complet pour Votre Entreprise
            </h1>
            <p className="text-xl text-text-gray leading-relaxed">
              Découvrez notre gamme complète de services comptables, fiscaux et de conseil 
              adaptés à vos besoins spécifiques.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mockData.services.map((service) => {
              const IconComponent = require('lucide-react')[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className={`service-card bg-white hover:bg-light-gray cursor-pointer ${service.specialty ? 'border-2 border-accent-blue' : ''}`}
                  onClick={() => setActiveService(activeService === service.id ? null : service.id)}
                >
                  <CardContent className="p-8">
                    {service.specialty && (
                      <div className="mb-4">
                        <Badge className="bg-accent-blue text-white px-3 py-1 text-sm">
                          Mission Spécialisée
                        </Badge>
                      </div>
                    )}
                    <div className="flex items-start space-x-4">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${service.specialty ? 'bg-accent-blue' : 'bg-light-blue'}`}>
                        <IconComponent className={`w-8 h-8 ${service.specialty ? 'text-white' : 'text-primary-blue'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-semibold text-text-dark mb-3">
                          {service.titre}
                        </h3>
                        <p className="text-text-gray leading-relaxed mb-4">
                          {service.description}
                        </p>
                        
                        {activeService === service.id && (
                          <div className="mt-6 pt-6 border-t">
                            <h4 className="font-semibold text-text-dark mb-4">Détails du service :</h4>
                            <ul className="space-y-2">
                              {service.details.map((detail) => (
                                <li key={detail} className="flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-primary-blue mt-1 flex-shrink-0" />
                                  <span className="text-text-gray text-sm">{detail}</span>
                                </li>
                              ))}
                            </ul>

                            {service.benefits && (
                              <div className="mt-6">
                                <h4 className="font-semibold text-text-dark mb-4">Bénéfices :</h4>
                                <ul className="space-y-2">
                                  {service.benefits.map((benefit) => (
                                    <li key={benefit} className="flex items-start space-x-2">
                                      <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                                      <span className="text-text-gray text-sm">{benefit}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {service.pricing && (
                              <div className="mt-6 p-4 bg-light-blue rounded-lg">
                                <h4 className="font-semibold text-primary-blue mb-2">{service.pricing.description}</h4>
                                <p className="text-sm text-primary-blue">{service.pricing.note}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <Button variant="ghost" className="p-0 h-auto text-primary-blue hover:text-accent-blue">
                            {activeService === service.id ? 'Voir moins' : 'En savoir plus'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stratégie de Rémunération - Section spéciale */}
      <section className="section-spacing bg-gradient-to-r from-primary-blue to-accent-blue text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-white text-primary-blue px-4 py-2">
                Mission Spécialisée
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Optimisez Votre Rémunération de Dirigeant
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Maximisez votre revenu net grâce à notre expertise en stratégie de rémunération : 
                une approche sur mesure pour optimiser fiscalement et socialement votre situation personnelle et professionnelle.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white bg-opacity-10 border-0 text-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-primary-blue" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Gains Concrets</h3>
                  <p className="text-blue-100">Jusqu'à 6% d'économie sur votre revenu net annuel</p>
                </CardContent>
              </Card>

              <Card className="bg-white bg-opacity-10 border-0 text-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-primary-blue" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Conformité Totale</h3>
                  <p className="text-blue-100">Solutions 100% conformes à la législation fiscale et sociale</p>
                </CardContent>
              </Card>

              <Card className="bg-white bg-opacity-10 border-0 text-white">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-primary-blue" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Accompagnement Complet</h3>
                  <p className="text-blue-100">De l'analyse à la mise en place, nous vous accompagnons à chaque étape</p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white bg-opacity-10 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6 text-center">Notre Approche Exclusive</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Analyse Personnalisée</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Étude de votre structure actuelle de rémunération</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Analyse comparative des différents statuts</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Prise en compte de vos droits à la retraite</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-4">Mise en Œuvre Sécurisée</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Accompagnement juridique complet</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Création de structures dédiées si nécessaire</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-200 mt-1 flex-shrink-0" />
                      <span className="text-blue-100 text-sm">Suivi et ajustements périodiques</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-blue-100 mb-6 text-lg">
                <strong>Notre engagement :</strong> Nous ne facturons cette mission que si elle vous fait gagner plus que nos honoraires la première année.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-white text-primary-blue hover:bg-blue-50 px-8 py-4 text-lg rounded-xl">
                  <Link to="/comparateur-statuts">
                    Comparer les statuts juridiques
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-blue px-8 py-4 text-lg rounded-xl">
                  <Link to="/contact">
                    Demander une étude personnalisée
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="section-spacing bg-light-gray">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
                Questions Fréquentes
              </h2>
              <p className="text-xl text-text-gray">
                Retrouvez les réponses aux questions les plus courantes sur nos services
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Quels sont vos tarifs ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Nos tarifs sont personnalisés selon la taille de votre entreprise et vos besoins spécifiques. 
                  Nous proposons des forfaits transparents sans surprise. Référez-vous à notre simulateur.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Dans quels délais traitez-vous les dossiers ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Nous nous engageons à répondre à vos demandes sous 24h. Les dossiers comptables sont traités 
                  mensuellement avec remise des documents sous 15 jours après réception de vos pièces.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Proposez-vous un accompagnement à la création d'entreprise ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Oui, nous accompagnons les entrepreneurs dans toutes les étapes : choix du statut juridique, 
                  formalités de création, business plan, et mise en place de la comptabilité.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Utilisez-vous des outils numériques ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Nous utilisons les derniers outils comptables sécurisés pour optimiser nos processus et vous offrir 
                  un accès temps réel à vos données comptables et fiscales.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Qu'est-ce que la mission "Stratégie de Rémunération" ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Cette mission spécialisée analyse votre structure de rémunération actuelle et explore toutes les options 
                  pour maximiser votre revenu net après impôts et cotisations. Nous étudions votre situation personnelle 
                  et professionnelle pour vous proposer la stratégie optimale en toute conformité légale.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Quels gains puis-je espérer avec cette mission ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Les gains varient selon votre situation, mais nos clients obtiennent généralement entre 3% et 6% 
                  d'économie sur leur revenu net annuel. Nous vous présentons une estimation chiffrée avant toute intervention. 
                  Si nous ne trouvons aucune optimisation rentable, nous ne vous facturons rien.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Cela peut-il pénaliser mes droits à la retraite ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Nous prenons systématiquement en compte l'impact de chaque option sur vos droits futurs. 
                  Notre approche trouve le bon équilibre entre optimisation immédiate et préservation de vos droits à la retraite. 
                  Vous recevrez une analyse chiffrée de l'impact sur vos droits.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-white rounded-xl border-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-text-dark hover:no-underline">
                  Intervenez-vous uniquement sur Lyon ?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-text-gray">
                  Nous intervenons sur toute la France ! Bien que notre cabinet soit basé à Lyon, nous accompagnons 
                  des entreprises partout en France grâce à nos services en full digital. Nos prestations peuvent 
                  être réalisées entièrement à distance via nos outils numériques sécurisés, ou en mode hybride 
                  avec des rendez-vous en visioconférence selon vos préférences.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
              Comment Ça Marche ?
            </h2>
            <p className="text-xl text-text-gray">
              Un processus simple et efficace pour démarrer notre collaboration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Premier Contact",
                description: "Prise de contact et échange gratuit sur vos besoins"
              },
              {
                step: "02",
                title: "Analyse",
                description: "Étude de votre situation et proposition personnalisée"
              },
              {
                step: "03",
                title: "Mise en Place",
                description: "Signature et démarrage de l'accompagnement"
              },
              {
                step: "04",
                title: "Suivi Régulier",
                description: "Accompagnement continu et points réguliers"
              }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-primary-blue text-white rounded-2xl flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-text-dark mb-3">
                  {item.title}
                </h3>
                <p className="text-text-gray text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à Confier Votre Comptabilité ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour discuter de vos besoins et obtenir un devis personnalisé
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-primary-blue hover:bg-blue-50 px-8 py-4 text-lg rounded-xl">
              <Link to="/contact">
                Prendre rendez-vous
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-blue px-8 py-4 text-lg rounded-xl">
              <Link to="/a-propos" className="flex items-center space-x-2">
                <span>En savoir plus sur nous</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;